import React, {
  useEffect,
  useState
} from 'react';

import {
  FaUsers,
  FaMoneyBillWave,
  FaWallet,
  FaGift,
  FaUserTie,
  FaChartLine,
  FaClock,
  FaDollarSign
} from 'react-icons/fa';

import StatsCard from '../components/cards/StatsCard';
import LineChart from '../components/charts/LineChart';
import PieChart from '../components/charts/PieChart';

import {
  getDashboardAnalytics
} from '../services/dashboardService';

import './Dashboard.css';

const Dashboard = () => {

  /*
  |--------------------------------------------------------------------------
  | STATES
  |--------------------------------------------------------------------------
  */

  const [loading, setLoading] =
    useState(true);

  const [dashboardData, setDashboardData] =
    useState({});

  const [lineChartData, setLineChartData] =
    useState([]);

  const [pieChartData, setPieChartData] =
    useState([]);

  const [
    recentTransactions,
    setRecentTransactions
  ] = useState([]);

  const [
    recentUsers,
    setRecentUsers
  ] = useState([]);

  /*
  |--------------------------------------------------------------------------
  | FETCH DASHBOARD
  |--------------------------------------------------------------------------
  */

  const fetchDashboardData = async () => {

    try {

      setLoading(true);

      const response =
        await getDashboardAnalytics();

      console.log(
        "Dashboard Response:",
        response
      );

      /*
      |--------------------------------------------------------------------------
      | SET STATS
      |--------------------------------------------------------------------------
      */

      setDashboardData(
        response.stats || {}
      );

      /*
      |--------------------------------------------------------------------------
      | SET CHARTS
      |--------------------------------------------------------------------------
      */

      setLineChartData(
        Array.isArray(response.lineChartData)
          ? response.lineChartData
          : []
      );

      setPieChartData(
        Array.isArray(response.pieChartData)
          ? response.pieChartData
          : []
      );

      /*
      |--------------------------------------------------------------------------
      | SET TRANSACTIONS
      |--------------------------------------------------------------------------
      */

      setRecentTransactions(
        Array.isArray(
          response.recentTransactions
        )
          ? response.recentTransactions
          : []
      );

      /*
      |--------------------------------------------------------------------------
      | SET USERS
      |--------------------------------------------------------------------------
      */

      setRecentUsers(
        Array.isArray(response.recentUsers)
          ? response.recentUsers
          : []
      );

    } catch (error) {

      console.log(
        "Dashboard Error:",
        error
      );

    } finally {

      setLoading(false);

    }
  };

  /*
  |--------------------------------------------------------------------------
  | ON LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    fetchDashboardData();

  }, []);

  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {

    return (
      <div className="dashboard-loading">
        Loading Dashboard...
      </div>
    );

  }

  return (
    <div className="dashboard">

      {/* HEADER */}
      <div className="dashboard-header">

        <h1>Dashboard Overview</h1>

        <p>
          Welcome back! Here's what's
          happening with your platform
          today.
        </p>

      </div>

      {/* STATS */}
      <div className="stats-grid">

        <StatsCard
          title="Total Users"
          value={
            dashboardData.totalUsers || 0
          }
          icon={<FaUsers />}
          color="primary"
          percentage="12.5"
          trend="up"
        />

        <StatsCard
          title="Total Deposits"
          value={`$${Number(
            dashboardData.totalDeposits || 0
          ).toLocaleString()}`}
          icon={<FaMoneyBillWave />}
          color="success"
          percentage="8.3"
          trend="up"
        />

        <StatsCard
          title="Total Withdrawals"
          value={`$${Number(
            dashboardData.totalWithdrawals || 0
          ).toLocaleString()}`}
          icon={<FaWallet />}
          color="warning"
          percentage="5.2"
          trend="down"
        />

        <StatsCard
          title="Rewards Distributed"
          value={`$${Number(
            dashboardData.rewardsDistributed || 0
          ).toLocaleString()}`}
          icon={<FaGift />}
          color="info"
          percentage="15.8"
          trend="up"
        />

        <StatsCard
          title="Total Salary Paid"
          value={`$${Number(
            dashboardData.totalSalaryPaid || 0
          ).toLocaleString()}`}
          icon={<FaUserTie />}
          color="danger"
          percentage="3.7"
          trend="up"
        />

        <StatsCard
          title="Active Users"
          value={
            dashboardData.activeUsers || 0
          }
          icon={<FaChartLine />}
          color="success"
          percentage="10.2"
          trend="up"
        />

        <StatsCard
          title="Pending Withdrawals"
          value={`$${Number(
            dashboardData.pendingWithdrawals || 0
          ).toLocaleString()}`}
          icon={<FaClock />}
          color="warning"
          percentage="2.1"
          trend="down"
        />

        <StatsCard
          title="Monthly Revenue"
          value={`$${Number(
            dashboardData.monthlyRevenue || 0
          ).toLocaleString()}`}
          icon={<FaDollarSign />}
          color="primary"
          percentage="18.5"
          trend="up"
        />

      </div>

      {/* CHARTS */}
      <div className="charts-row">

        <div className="chart-col">

          <LineChart
            data={lineChartData}
            title="Deposits & Withdrawals Overview"
          />

        </div>

        <div className="chart-col">

          <PieChart
            data={pieChartData}
            title="User Statistics"
          />

        </div>

      </div>

      {/* TABLES */}
      <div className="tables-row">

        {/* TRANSACTIONS */}
        <div className="table-container">

          <div className="table-header">

            <h5>
              Recent Transactions
            </h5>

            <a
              href="/transactions"
              className="view-all-link"
            >
              View All
            </a>

          </div>

          <div className="table-responsive">

            <table className="table">

              <thead>

                <tr>
                  <th>Txn Hash</th>
                  <th>Deposit</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>

              </thead>

              <tbody>

                {recentTransactions.length >
                  0 ? (

                  recentTransactions.map(
                    (transaction) => (

                      <tr
                        key={transaction.id}
                      >

                        <td>
                          {transaction.wallet
                            ? `${transaction.wallet.slice(0, 5)}...${transaction.wallet.slice(-5)}`
                            : 'N/A'}
                        </td>



                        <td className="font-weight-bold">

                          $
                          {Number(
                            transaction.deposit || 0
                          ).toLocaleString()}

                        </td>

                        <td>

                          <span
                            className={`status-badge status-${(
                              transaction.status ||
                              ''
                            ).toLowerCase()}`}
                          >

                            {
                              transaction.status
                            }

                          </span>

                        </td>

                        <td>
                          {transaction.date}
                        </td>

                      </tr>

                    )
                  )

                ) : (

                  <tr>

                    <td
                      colSpan="5"
                      className="text-center"
                    >
                      No Transactions Found
                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

        </div>

        {/* USERS */}
        <div className="table-container">

          <div className="table-header">

            <h5>
              Recent User Registrations
            </h5>

            <a
              href="/users"
              className="view-all-link"
            >
              View All
            </a>

          </div>

          <div className="table-responsive">

            <table className="table">

              <thead>

                <tr>
                  <th>Wallet</th>
                  <th>Deposit</th>
                  <th>Status</th>
                  <th>Joined</th>
                </tr>

              </thead>

              <tbody>

                {recentUsers.length > 0 ? (

                  recentUsers.map((user) => (

                    <tr key={user.id}>

                      <td>
                        {user.wallet
                          ? `${user.wallet.slice(0, 5)}...${user.wallet.slice(-5)}`
                          : 'N/A'}
                      </td>

                      <td>${user.deposit}</td>

                      <td>

                        <span
                          className={`status-badge status-${(
                            user.status || ''
                          ).toLowerCase()}`}
                        >

                          {user.status}

                        </span>

                      </td>

                      <td>
                        {user.joined}
                      </td>

                    </tr>

                  ))

                ) : (

                  <tr>

                    <td
                      colSpan="4"
                      className="text-center"
                    >
                      No Users Found
                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

        </div>

      </div>

    </div>
  );
};

export default Dashboard;